// src/pages/Page.js - Updated with live editing functionality
import axios from "axios";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate, useParams } from "react-router-dom";
import PageSection from "../components/admin/page-management/PageSection";
import LiveEditAdminBar from "../components/LiveEditAdminBar";
import PageSkeletonLoader from "../components/PageSkeletonLoader";
import SectionEditOverlay from "../components/SectionEditOverlay";
import { useAuth } from "../context/AuthContext";
import { useConfig } from "../context/ConfigContext";

const Page = () => {
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { slug } = useParams();
  const navigate = useNavigate();
  const { config } = useConfig();
  const { isAdmin } = useAuth();

  // Get site name from config
  const siteName = config?.siteName || " ";

  // useEffect(() => {
  //   const fetchPage = async () => {
  //     try {
  //       setLoading(true);
  //       const response = await axios.get(`/api/pages/public/${slug}`);
  //       setPage(response.data);

  //       // Set the document title using the site name from config
  //       document.title = `${response.data.title} | ${siteName}`;

  //       setError(null);
  //     } catch (err) {
  //       console.error("Error fetching page:", err);
  //       setError(
  //         "Page not found. It may have been removed or the URL is incorrect."
  //       );
  //       if (err.response && err.response.status === 404) {
  //         // Page not found - you could redirect to a 404 page here
  //       }
  //     } finally {
  //       setLoading(false);
  //     }
  //   };

  //   if (slug) {
  //     fetchPage();
  //   }
  // }, [slug, navigate, siteName]);
  useEffect(() => {
    const fetchPage = async () => {
      try {
        setLoading(true);
        // Handle home page case - if no slug, fetch the home page
        const endpoint = slug ? `/api/pages/public/${slug}` : "/api/pages/home";
        const response = await axios.get(endpoint);
        setPage(response.data);

        // Set the document title using the site name from config
        document.title = `${response.data.title} | ${siteName}`;

        setError(null);
      } catch (err) {
        console.error("Error fetching page:", err);
        setError(
          "Page not found. It may have been removed or the URL is incorrect."
        );
      } finally {
        setLoading(false);
      }
    };

    // Always fetch - either with slug or as home page
    fetchPage();
  }, [slug, navigate, siteName]);

  // Helper function to find child sections for a given parent ID
  const findChildSections = (parentId) => {
    if (!page || !page.sections) return [];

    return page.sections
      .filter(
        (section) =>
          section.parentId &&
          section.parentId.toString() === parentId.toString()
      )
      .sort((a, b) => a.order - b.order);
  };

  // Handle section edit actions from overlay
  const handleSectionEdit = async (action, sectionId, direction = null) => {
    if (!isAdmin || !page) return;

    try {
      let response;

      switch (action) {
        case "delete":
          response = await axios.delete(
            `/api/admin/pages/${page._id}/sections/${sectionId}`
          );
          setPage(response.data);
          break;

        case "move":
          // Get the section to move
          const section = page.sections.find((s) => s._id === sectionId);
          if (!section) return;

          // Get all sections at the same level
          const siblingCondition = section.parentId
            ? (s) =>
              s.parentId &&
              s.parentId.toString() === section.parentId.toString()
            : (s) => !s.parentId;

          const sortedSections = [...page.sections]
            .filter(siblingCondition)
            .sort((a, b) => a.order - b.order);

          const sectionIndex = sortedSections.findIndex(
            (s) => s._id === sectionId
          );
          const targetIndex =
            direction === "up" ? sectionIndex - 1 : sectionIndex + 1;

          if (targetIndex >= 0 && targetIndex < sortedSections.length) {
            const sectionIds = sortedSections.map((s) => s._id);
            [sectionIds[sectionIndex], sectionIds[targetIndex]] = [
              sectionIds[targetIndex],
              sectionIds[sectionIndex],
            ];

            response = await axios.post(
              `/api/admin/pages/${page._id}/sections/reorder`,
              {
                sectionIds,
                parentId: section.parentId,
              }
            );
            setPage(response.data);
          }
          break;

        case "duplicate":
          // Get the section to duplicate
          const sectionToDuplicate = page.sections.find(
            (s) => s._id === sectionId
          );
          if (sectionToDuplicate) {
            const duplicatedSection = {
              type: sectionToDuplicate.type,
              content: JSON.parse(JSON.stringify(sectionToDuplicate.content)),
              settings: JSON.parse(JSON.stringify(sectionToDuplicate.settings)),
              parentId: sectionToDuplicate.parentId,
              order: sectionToDuplicate.order + 1,
            };

            response = await axios.post(
              `/api/admin/pages/${page._id}/sections`,
              duplicatedSection
            );
            setPage(response.data);
          }
          break;

        default:
          console.log("Unknown action:", action);
      }
    } catch (err) {
      console.error("Error handling section edit:", err);
      // You might want to show a notification here
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <PageSkeletonLoader />
      </div>
    );
  }

  if (error) {
    const timer = setTimeout(() => {
      navigate("/");
    }, 1000);

    return (
      <div className="min-h-screen flex flex-col justify-center items-center">
        <div className="w-64 h-64 mb-6">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 300">
            {/* Background */}
            <rect width="400" height="300" fill="#f8fafc" rx="10" ry="10" />

            {/* Document with fold */}
            <path
              d="M130 80 L270 80 L270 220 L130 220 Z"
              fill="#e2e8f0"
              stroke="#cbd5e1"
              strokeWidth="2"
            />
            <path
              d="M220 80 L270 130 L220 130 Z"
              fill="#cbd5e1"
              stroke="#cbd5e1"
              strokeWidth="1"
            />

            {/* Question mark */}
            <circle cx="200" cy="160" r="40" fill="#f1f5f9" />
            <path
              d="M190 150 Q200 130 210 150 Q210 160 200 165 L200 180"
              stroke="#475569"
              strokeWidth="8"
              strokeLinecap="round"
              fill="none"
            />
            <circle cx="200" cy="195" r="4" fill="#475569" />

            {/* X mark top right corner */}
            <line
              x1="250"
              y1="100"
              x2="240"
              y2="110"
              stroke="#94a3b8"
              strokeWidth="3"
              strokeLinecap="round"
            />
            <line
              x1="240"
              y1="100"
              x2="250"
              y2="110"
              stroke="#94a3b8"
              strokeWidth="3"
              strokeLinecap="round"
            />

            {/* Error text */}
            <text
              x="200"
              y="250"
              fontFamily="Arial, sans-serif"
              fontSize="16"
              textAnchor="middle"
              fill="#475569"
              fontWeight="bold"
            >
              Page not found
            </text>
          </svg>
        </div>
        <p className="text-gray-700 mt-2 mb-6">{error}</p>
        <button
          onClick={() => navigate("/")}
          className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-lg transition-colors duration-200"
          style={{ backgroundColor: config?.colors?.primary }}
        >
          Return to Home
        </button>
      </div>
    );
  }

  if (!page) {
    setTimeout(() => {
      navigate("/");
    }, 1000);
    return (
      <div className="min-h-screen flex flex-col justify-center items-center h-64">
        <div className="w-64 h-64 mb-6">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 300">
            {/* Background */}
            <rect width="400" height="300" fill="#f8fafc" rx="10" ry="10" />

            {/* Document with fold */}
            <path
              d="M130 80 L270 80 L270 220 L130 220 Z"
              fill="#e2e8f0"
              stroke="#cbd5e1"
              strokeWidth="2"
            />
            <path
              d="M220 80 L270 130 L220 130 Z"
              fill="#cbd5e1"
              stroke="#cbd5e1"
              strokeWidth="1"
            />

            {/* Question mark */}
            <circle cx="200" cy="160" r="40" fill="#f1f5f9" />
            <path
              d="M190 150 Q200 130 210 150 Q210 160 200 165 L200 180"
              stroke="#475569"
              strokeWidth="8"
              strokeLinecap="round"
              fill="none"
            />
            <circle cx="200" cy="195" r="4" fill="#475569" />

            {/* X mark top right corner */}
            <line
              x1="250"
              y1="100"
              x2="240"
              y2="110"
              stroke="#94a3b8"
              strokeWidth="3"
              strokeLinecap="round"
            />
            <line
              x1="240"
              y1="100"
              x2="250"
              y2="110"
              stroke="#94a3b8"
              strokeWidth="3"
              strokeLinecap="round"
            />

            {/* Error text */}
            <text
              x="200"
              y="250"
              fontFamily="Arial, sans-serif"
              fontSize="16"
              textAnchor="middle"
              fill="#475569"
              fontWeight="bold"
            >
              Page not found
            </text>
          </svg>
        </div>
        <p className="text-gray-700 mt-2 mb-6">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <button
          onClick={() => navigate("/")}
          className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-lg transition-colors duration-200"
          style={{ backgroundColor: config?.colors?.primary }}
        >
          Return to Home
        </button>
      </div>
    );
  }

  // Determine the container class based on the template
  const containerClass =
    page.template === "full-width"
      ? "w-full"
      : "container min-h-screen mx-auto px-4";

  const getPageBackgroundStyle = () => {
    if (!page) return {};

    const style = {};

    switch (page.backgroundType) {
      case "color":
        if (page.backgroundColor) {
          style.backgroundColor = page.backgroundColor;
        }
        break;

      case "image":
        if (page.backgroundImage) {
          style.backgroundImage = `url(${page.backgroundImage})`;
          style.backgroundSize = page.backgroundSize || "cover";
          style.backgroundPosition = page.backgroundPosition || "center";
          style.backgroundRepeat = page.backgroundRepeat || "no-repeat";
          style.backgroundAttachment = page.backgroundAttachment || "scroll";
        }
        break;

      case "video":
        // Video background is handled with a separate element
        break;

      default:
        break;
    }

    return style;
  };

  // Get top-level sections for the admin bar
  const topLevelSections = page.sections
    ? page.sections
      .filter((section) => !section.parentId)
      .sort((a, b) => a.order - b.order)
    : [];

  return (
    <>
      <Helmet>
        <title>{`${page.metaTitle || page.title} | ${siteName}`}</title>
        <meta
          name="description"
          content={page.metaDescription || page.description}
        />
      </Helmet>

      {/* Live Edit Admin Bar - only shown to admins */}
      {isAdmin && (
        <LiveEditAdminBar page={page} sections={page.sections || []} />
      )}

      {/* Video Background (if applicable) */}
      {page.backgroundType === "video" && page.backgroundVideo && (
        <div className="fixed inset-0 w-full h-full z-[-1] overflow-hidden">
          <video
            src={page.backgroundVideo}
            className="absolute min-w-full min-h-full object-cover"
            autoPlay={page.videoAutoplay !== false}
            muted={page.videoMuted !== false}
            loop={page.videoLoop !== false}
            playsInline
          />
        </div>
      )}

      {/* Background Overlay (if enabled) */}
      {page.backgroundOverlay?.enabled && (
        <div
          className="fixed inset-0 w-full h-full z-[-1]"
          style={{
            backgroundColor: page.backgroundOverlay.color || "#000000",
            opacity: page.backgroundOverlay.opacity || 0.5,
            pointerEvents: "none",
          }}
        />
      )}

      <div className={containerClass} style={getPageBackgroundStyle()}>
        {page.sections && page.sections.length > 0 ? (
          topLevelSections.map((section, index) => {
            // For container sections, we need to pass the child sections
            if (section.type === "container") {
              // Find all child sections
              const childSections = findChildSections(section._id);

              // Create an enhanced section object with children
              const enhancedSection = {
                ...section,
                children: childSections,
              };

              return (
                <div key={section._id} className="relative">
                  {/* Section Edit Overlay for admins */}
                  {isAdmin && (
                    <SectionEditOverlay
                      section={section}
                      pageId={page._id}
                      sectionIndex={index}
                      totalSections={topLevelSections.length}
                      onSectionEdit={handleSectionEdit}
                    />
                  )}
                  <PageSection section={enhancedSection} />
                </div>
              );
            }

            // For regular sections, render normally
            return (
              <div key={section._id} className="relative">
                {/* Section Edit Overlay for admins */}
                {isAdmin && (
                  <SectionEditOverlay
                    section={section}
                    pageId={page._id}
                    sectionIndex={index}
                    totalSections={topLevelSections.length}
                    onSectionEdit={handleSectionEdit}
                  />
                )}
                <PageSection section={section} />
              </div>
            );
          })
        ) : (
          <div className="min-h-screen py-12 text-center">
            <h1 className="text-3xl font-bold">{page.title}</h1>
            {page.description && <p className="mt-4">{page.description}</p>}
            <p className="mt-8 text-gray-500">
              This page has no content sections yet.
            </p>

            {/* Quick add section button for admins */}
            {isAdmin && (
              <button
                onClick={() => navigate(`/pitech/pages/${page._id}`)}
                className="mt-4 bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg"
              >
                Add Your First Section
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default Page;

// // before live edit
// // src/pages/Page.js
// import React, { useState, useEffect } from "react";
// import { useParams, useNavigate } from "react-router-dom";
// import axios from "axios";
// import { Helmet } from "react-helmet";
// import PageSection from "../components/admin/page-management/PageSection";
// import PageSkeletonLoader from "../components/PageSkeletonLoader";
// import { useConfig } from "../context/ConfigContext"; // Import the context hook

// const Page = () => {
//   const [page, setPage] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const { slug } = useParams();
//   const navigate = useNavigate();

//   // Get the config data using our context hook
//   const { config } = useConfig();

//   // Get site name from config instead of making a separate API call
//   const siteName = config?.siteName || " ";

//   useEffect(() => {
//     const fetchPage = async () => {
//       try {
//         setLoading(true);
//         const response = await axios.get(`/api/pages/public/${slug}`);
//         setPage(response.data);

//         // Set the document title using the site name from config
//         document.title = `${response.data.title} | ${siteName}`;

//         setError(null);
//       } catch (err) {
//         console.error("Error fetching page:", err);
//         setError(
//           "Page not found. It may have been removed or the URL is incorrect."
//         );
//         if (err.response && err.response.status === 404) {
//           // Page not found - you could redirect to a 404 page here
//           // or just show an error message
//         }
//       } finally {
//         setLoading(false);
//       }
//     };

//     if (slug) {
//       fetchPage();
//     }
//   }, [slug, navigate, siteName]); // Add siteName to the dependency array

//   // Helper function to find child sections for a given parent ID
//   const findChildSections = (parentId) => {
//     if (!page || !page.sections) return [];

//     return page.sections
//       .filter(
//         (section) =>
//           section.parentId &&
//           section.parentId.toString() === parentId.toString()
//       )
//       .sort((a, b) => a.order - b.order);
//   };

//   if (loading) {
//     return (
//       <div className="flex justify-center items-center min-h-screen">
//         <PageSkeletonLoader />
//       </div>
//     );
//   }

//   if (error) {
//     const timer = setTimeout(() => {
//       navigate("/");
//     }, 1000);

//     return (
//       <div className="min-h-screen flex flex-col justify-center items-center">
//         <div className="w-64 h-64 mb-6">
//           <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 300">
//             {/* Background */}
//             <rect width="400" height="300" fill="#f8fafc" rx="10" ry="10" />

//             {/* Document with fold */}
//             <path
//               d="M130 80 L270 80 L270 220 L130 220 Z"
//               fill="#e2e8f0"
//               stroke="#cbd5e1"
//               strokeWidth="2"
//             />
//             <path
//               d="M220 80 L270 130 L220 130 Z"
//               fill="#cbd5e1"
//               stroke="#cbd5e1"
//               strokeWidth="1"
//             />

//             {/* Question mark */}
//             <circle cx="200" cy="160" r="40" fill="#f1f5f9" />
//             <path
//               d="M190 150 Q200 130 210 150 Q210 160 200 165 L200 180"
//               stroke="#475569"
//               strokeWidth="8"
//               strokeLinecap="round"
//               fill="none"
//             />
//             <circle cx="200" cy="195" r="4" fill="#475569" />

//             {/* X mark top right corner */}
//             <line
//               x1="250"
//               y1="100"
//               x2="240"
//               y2="110"
//               stroke="#94a3b8"
//               strokeWidth="3"
//               strokeLinecap="round"
//             />
//             <line
//               x1="240"
//               y1="100"
//               x2="250"
//               y2="110"
//               stroke="#94a3b8"
//               strokeWidth="3"
//               strokeLinecap="round"
//             />

//             {/* Error text */}
//             <text
//               x="200"
//               y="250"
//               fontFamily="Arial, sans-serif"
//               fontSize="16"
//               textAnchor="middle"
//               fill="#475569"
//               fontWeight="bold"
//             >
//               Page not found
//             </text>
//           </svg>
//         </div>
//         <p className="text-gray-700 mt-2 mb-6">{error}</p>
//         <button
//           onClick={() => navigate("/")}
//           className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-lg transition-colors duration-200"
//           style={{ backgroundColor: config?.colors?.primary }} // Use primary color from config
//         >
//           Return to Home
//         </button>
//       </div>
//     );
//   }

//   if (!page) {
//     setTimeout(() => {
//       navigate("/");
//     }, 1000);
//     return (
//       <div className="min-h-screen flex flex-col justify-center items-center h-64">
//         <div className="w-64 h-64 mb-6">
//           <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 300">
//             {/* Background */}
//             <rect width="400" height="300" fill="#f8fafc" rx="10" ry="10" />

//             {/* Document with fold */}
//             <path
//               d="M130 80 L270 80 L270 220 L130 220 Z"
//               fill="#e2e8f0"
//               stroke="#cbd5e1"
//               strokeWidth="2"
//             />
//             <path
//               d="M220 80 L270 130 L220 130 Z"
//               fill="#cbd5e1"
//               stroke="#cbd5e1"
//               strokeWidth="1"
//             />

//             {/* Question mark */}
//             <circle cx="200" cy="160" r="40" fill="#f1f5f9" />
//             <path
//               d="M190 150 Q200 130 210 150 Q210 160 200 165 L200 180"
//               stroke="#475569"
//               strokeWidth="8"
//               strokeLinecap="round"
//               fill="none"
//             />
//             <circle cx="200" cy="195" r="4" fill="#475569" />

//             {/* X mark top right corner */}
//             <line
//               x1="250"
//               y1="100"
//               x2="240"
//               y2="110"
//               stroke="#94a3b8"
//               strokeWidth="3"
//               strokeLinecap="round"
//             />
//             <line
//               x1="240"
//               y1="100"
//               x2="250"
//               y2="110"
//               stroke="#94a3b8"
//               strokeWidth="3"
//               strokeLinecap="round"
//             />

//             {/* Error text */}
//             <text
//               x="200"
//               y="250"
//               fontFamily="Arial, sans-serif"
//               fontSize="16"
//               textAnchor="middle"
//               fill="#475569"
//               fontWeight="bold"
//             >
//               Page not found
//             </text>
//           </svg>
//         </div>
//         <p className="text-gray-700 mt-2 mb-6">
//           The page you're looking for doesn't exist or has been moved.
//         </p>
//         <button
//           onClick={() => navigate("/")}
//           className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-lg transition-colors duration-200"
//           style={{ backgroundColor: config?.colors?.primary }} // Use primary color from config
//         >
//           Return to Home
//         </button>
//       </div>
//     );
//   }

//   // Determine the container class based on the template
//   const containerClass =
//     page.template === "full-width"
//       ? "w-full"
//       : "container min-h-screen mx-auto px-4";

//   const getPageBackgroundStyle = () => {
//     if (!page) return {};

//     const style = {};

//     switch (page.backgroundType) {
//       case "color":
//         if (page.backgroundColor) {
//           style.backgroundColor = page.backgroundColor;
//         }
//         break;

//       case "image":
//         if (page.backgroundImage) {
//           style.backgroundImage = `url(${page.backgroundImage})`;
//           style.backgroundSize = page.backgroundSize || "cover";
//           style.backgroundPosition = page.backgroundPosition || "center";
//           style.backgroundRepeat = page.backgroundRepeat || "no-repeat";
//           style.backgroundAttachment = page.backgroundAttachment || "scroll";
//         }
//         break;

//       case "video":
//         // Video background is handled with a separate element
//         break;

//       default:
//         break;
//     }

//     return style;
//   };

//   return (
//     <>
//       <Helmet>
//         <title>{`${page.metaTitle || page.title} | ${siteName}`}</title>
//         <meta
//           name="description"
//           content={page.metaDescription || page.description}
//         />
//       </Helmet>

//       {/* Video Background (if applicable) */}
//       {page.backgroundType === "video" && page.backgroundVideo && (
//         <div className="fixed inset-0 w-full h-full z-[-1] overflow-hidden">
//           <video
//             src={page.backgroundVideo}
//             className="absolute min-w-full min-h-full object-cover"
//             autoPlay={page.videoAutoplay !== false}
//             muted={page.videoMuted !== false}
//             loop={page.videoLoop !== false}
//             playsInline
//           />
//         </div>
//       )}

//       {/* Background Overlay (if enabled) */}
//       {page.backgroundOverlay?.enabled && (
//         <div
//           className="fixed inset-0 w-full h-full z-[-1]"
//           style={{
//             backgroundColor: page.backgroundOverlay.color || "#000000",
//             opacity: page.backgroundOverlay.opacity || 0.5,
//             pointerEvents: "none",
//           }}
//         />
//       )}

//       <div className={containerClass} style={getPageBackgroundStyle()}>
//         {page.sections && page.sections.length > 0 ? (
//           page.sections
//             .filter((section) => !section.parentId) // Only render top-level sections
//             .sort((a, b) => a.order - b.order)
//             .map((section) => {
//               // For container sections, we need to pass the child sections
//               if (section.type === "container") {
//                 // Find all child sections
//                 const childSections = findChildSections(section._id);

//                 // Create an enhanced section object with children
//                 const enhancedSection = {
//                   ...section,
//                   children: childSections,
//                 };

//                 return (
//                   <PageSection key={section._id} section={enhancedSection} />
//                 );
//               }

//               // For regular sections, render normally
//               return <PageSection key={section._id} section={section} />;
//             })
//         ) : (
//           <div className="min-h-screen py-12 text-center">
//             <h1 className="text-3xl font-bold">{page.title}</h1>
//             {page.description && <p className="mt-4">{page.description}</p>}
//             <p className="mt-8 text-gray-500">
//               This page has no content sections yet.
//             </p>
//           </div>
//         )}
//       </div>
//     </>
//   );
// };

// export default Page;
